import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { BookOpen, Heart, Users, Eye, Star, Quote } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AboutPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      <Navbar />

      {/* Hero */}
      <section className="relative py-20 px-4 overflow-hidden" style={{background: 'linear-gradient(135deg, #A89968 0%, #c4b07d 50%, #d4c494 100%)'}}>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="about-hero-title">About Us</h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">India's first peer-to-peer book rental platform</p>
        </div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10"><BookOpen size={80} className="text-white" /></div>
          <div className="absolute bottom-10 right-20"><BookOpen size={60} className="text-white" /></div>
          <div className="absolute top-20 right-40"><BookOpen size={40} className="text-white" /></div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">

        {/* Who We Are */}
        <section data-testid="about-who-we-are">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{backgroundColor: 'var(--secondary)'}}>
              <Users className="h-6 w-6" style={{color: 'var(--primary)'}} />
            </div>
            <h2 className="text-3xl font-bold" style={{color: 'var(--primary)'}}>Who We Are</h2>
          </div>
          <p className="text-gray-700 leading-relaxed text-lg">
            mybook.rent is India's first peer-to-peer book rental platform, where book lovers can rent books from other book lovers, right in their own neighbourhood. We believe that a great story should never be out of reach simply because of its price tag, and that the books sitting quietly on your shelf deserve to be read again and again.
          </p>
        </section>

        {/* How It All Started */}
        <section className="bg-white rounded-2xl shadow-md p-8" data-testid="about-how-started">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{backgroundColor: '#FEF3C7'}}>
              <Star className="h-6 w-6 text-amber-600" />
            </div>
            <h2 className="text-3xl font-bold" style={{color: 'var(--primary)'}}>How It All Started</h2>
          </div>
          <p className="text-gray-700 leading-relaxed text-lg">
            It started, like most honest ideas do, at home. Kalyan was looking at a bookshelf full of books &mdash; good books, loved books &mdash; gathering dust. Books that had been read once, maybe twice, and were now just sitting there. And somewhere across the city, he knew there were readers who would love those very books but couldn't justify the cost of buying them new. And even when the pocket permits, readers rarely have enough space at home to keep adding to their shelves.
          </p>
          <p className="text-gray-700 leading-relaxed text-lg mt-4">
            That got him thinking &mdash; what if there was a better way? A way that gives book lovers more access to books at an affordable price, while giving book owners an opportunity to earn from what they already have. A shift from Consumer to Prosumer &mdash; where every reader can also become an earner. A genuine win-win.
          </p>
          <p className="text-gray-700 leading-relaxed text-lg mt-4 font-medium" style={{color: 'var(--primary)'}}>
            That gap &mdash; between a full bookshelf, an empty wallet, and the idea that a reader could become a prosumer &mdash; is what became mybook.rent.
          </p>
        </section>

        {/* What We Do */}
        <section data-testid="about-what-we-do">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{backgroundColor: 'var(--secondary)'}}>
              <BookOpen className="h-6 w-6" style={{color: 'var(--primary)'}} />
            </div>
            <h2 className="text-3xl font-bold" style={{color: 'var(--primary)'}}>What We Do</h2>
          </div>
          <p className="text-gray-700 leading-relaxed text-lg">
            mybook.rent is a peer-to-peer book rental platform. The books on our platform don't belong to us &mdash; they belong to people like you. Readers who have finished their books and want to share them. Neighbours who want to earn a little from the stories they've already loved.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border-l-4" style={{borderColor: 'var(--primary)'}}>
              <h3 className="font-bold text-lg mb-2" style={{color: 'var(--primary)'}}>For Lenders</h3>
              <p className="text-gray-600">People who list their personal book collections and earn every time someone borrows from them.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border-l-4" style={{borderColor: 'var(--accent)'}}>
              <h3 className="font-bold text-lg mb-2" style={{color: 'var(--accent)'}}>For Borrowers</h3>
              <p className="text-gray-600">Readers who get access to a wide, ever-growing library at a fraction of the cost of buying.</p>
            </div>
          </div>
          <p className="text-gray-700 leading-relaxed text-lg mt-6 text-center font-medium">We simply bring them together.</p>
        </section>

        {/* Why We Exist */}
        <section className="bg-white rounded-2xl shadow-md p-8" data-testid="about-why-exist">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{backgroundColor: '#DBEAFE'}}>
              <Heart className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold" style={{color: 'var(--primary)'}}>Why We Exist</h2>
          </div>
          <p className="text-gray-700 leading-relaxed text-lg">
            India has millions of passionate readers. It also has millions of households where books sit unread after the first read. We exist because we believe these two realities should meet &mdash; in the same neighbourhood, on the same street, sometimes in the same apartment building.
          </p>
          <p className="text-gray-700 leading-relaxed text-lg mt-4 font-medium italic" style={{color: 'var(--primary)'}}>
            Reading should not be a luxury. Sharing should not be complicated. And your bookshelf &mdash; the one you've built over years &mdash; should never stop giving.
          </p>
        </section>

        {/* Our Promise */}
        <section data-testid="about-promise">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{backgroundColor: '#F3E8FF'}}>
              <Star className="h-6 w-6 text-purple-600" />
            </div>
            <h2 className="text-3xl font-bold" style={{color: 'var(--primary)'}}>Our Promise</h2>
          </div>
          <div className="space-y-4">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <p className="text-gray-700 text-lg"><strong style={{color: 'var(--primary)'}}>To every borrower</strong> &mdash; affordable access to books you'll love, from readers who already loved them.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <p className="text-gray-700 text-lg"><strong style={{color: 'var(--accent)'}}>To every lender</strong> &mdash; a simple, trustworthy way to share your collection and earn from it.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <p className="text-gray-700 text-lg"><strong style={{color: 'var(--primary)'}}>To our community</strong> &mdash; a platform built with care, honesty, and a genuine love for books.</p>
            </div>
          </div>
        </section>

        {/* Our Vision */}
        <section className="text-center py-12 px-8 rounded-2xl" style={{background: 'linear-gradient(135deg, #A89968 0%, #c4b07d 100%)'}} data-testid="about-vision">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <Eye className="h-8 w-8 text-white" />
            <h2 className="text-3xl font-bold text-white">Our Vision</h2>
          </div>
          <p className="text-2xl font-medium text-white/95 italic max-w-2xl mx-auto">
            "Discovering a world of stories right in your neighbourhood."
          </p>
          <p className="text-white/80 mt-4 text-lg">
            We started in Bengaluru. But every city has bookshelves full of unread stories and readers hungry for their next one. mybook.rent is built to reach them all &mdash; one neighbourhood at a time.
          </p>
        </section>

        {/* A Note from Kalyan */}
        <section className="bg-white rounded-2xl shadow-md p-8" data-testid="about-note-kalyan">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{backgroundColor: 'var(--secondary)'}}>
              <Quote className="h-6 w-6" style={{color: 'var(--primary)'}} />
            </div>
            <h2 className="text-3xl font-bold" style={{color: 'var(--primary)'}}>A Note from Kalyan</h2>
          </div>
          <blockquote className="text-gray-700 leading-relaxed text-lg italic border-l-4 pl-6" style={{borderColor: 'var(--primary)'}}>
            "I didn't set out to build a tech company. I set out to solve something that bothered me &mdash; books I loved, sitting unused, while I knew someone out there would love them too. mybook.rent is that solution. Simple, community-driven, and built for every reader in India &mdash; regardless of what they can afford."
          </blockquote>
        </section>

        {/* CTA */}
        <section className="text-center py-8">
          <button
            onClick={() => navigate('/')}
            className="px-10 py-4 rounded-xl text-white text-lg font-semibold hover:opacity-90 transition-opacity"
            style={{backgroundColor: 'var(--primary)'}}
            data-testid="about-browse-books-btn"
          >
            Start Browsing Books
          </button>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;
